import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Bell, Loader2, Mail, MessageSquare } from "lucide-react";

/**
 * Admin tool: re-send booking confirmation notifications (email + optional SMS)
 * for every booking that falls within a date/time range. Use when notifications
 * were missed due to an outage or misconfiguration.
 */
export const ResendNotificationsCard = () => {
  const { toast } = useToast();
  const today = new Date().toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(today);
  const [startTime, setStartTime] = useState("00:00");
  const [endDate, setEndDate] = useState(today);
  const [endTime, setEndTime] = useState("23:59");
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(false);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [lastResult, setLastResult] = useState<{
    emailsSent: number;
    smsSent: number;
    failures: number;
    skipped: number;
  } | null>(null);

  const toMinutes = (t: string) => {
    const [h, m] = t.split(":").map(Number);
    return (h || 0) * 60 + (m || 0);
  };

  const parseBookingTimeToMinutes = (s: string): number | null => {
    if (!s) return null;
    const trimmed = s.trim();
    // 24-hour "HH:MM"
    const m24 = trimmed.match(/^(\d{1,2}):(\d{2})$/);
    if (m24) return Number(m24[1]) * 60 + Number(m24[2]);
    // 12-hour "h:MM AM/PM"
    const m12 = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (m12) {
      let h = Number(m12[1]) % 12;
      if (m12[3].toUpperCase() === "PM") h += 12;
      return h * 60 + Number(m12[2]);
    }
    return null;
  };

  const handleResend = async () => {
    if (!sendEmail && !sendSms) {
      toast({
        title: "Pick at least one channel",
        description: "Enable Email or SMS before resending.",
        variant: "destructive",
      });
      return;
    }
    if (startDate > endDate || (startDate === endDate && toMinutes(startTime) > toMinutes(endTime))) {
      toast({
        title: "Invalid range",
        description: "Start must be before end.",
        variant: "destructive",
      });
      return;
    }

    setBusy(true);
    setProgress(null);
    setLastResult(null);

    try {
      const { data: bookings, error } = await supabase
        .from("bookings")
        .select("id, user_id, service_name, service_category, booking_date, booking_time, duration, price, status").is("deleted_at", null)
        .gte("booking_date", startDate)
        .lte("booking_date", endDate)
        .order("booking_date", { ascending: true })
        .order("booking_time", { ascending: true });

      if (error) throw error;

      const startMin = toMinutes(startTime);
      const endMin = toMinutes(endTime);

      const filtered = (bookings || []).filter((b) => {
        if (!includeCancelled && b.status === "cancelled") return false;
        const mins = parseBookingTimeToMinutes(b.booking_time);
        if (mins == null) return true; // include unparsable times rather than skip silently
        if (b.booking_date === startDate && mins < startMin) return false;
        if (b.booking_date === endDate && mins > endMin) return false;
        return true;
      });

      if (filtered.length === 0) {
        toast({ title: "No bookings found", description: "No bookings match that range." });
        setBusy(false);
        return;
      }

      // Fetch profiles for recipients
      const userIds = Array.from(new Set(filtered.map((b) => b.user_id)));
      const { data: profileRows } = await supabase
        .from("profiles")
        .select("user_id, email, phone, referral_code, full_name")
        .in("user_id", userIds);
      const profiles = new Map((profileRows || []).map((p) => [p.user_id, p]));

      let emailsSent = 0;
      let smsSent = 0;
      let failures = 0;
      let skipped = 0;

      setProgress({ done: 0, total: filtered.length });

      for (let i = 0; i < filtered.length; i++) {
        const b = filtered[i];
        const profile = profiles.get(b.user_id);
        const type = b.status === "cancelled" ? "cancellation" : "confirmation";

        let didSomething = false;

        if (sendEmail && profile?.email) {
          try {
            const { error: emailErr } = await supabase.functions.invoke("send-booking-email", {
              body: {
                type,
                email: profile.email,
                client_name: profile.full_name || undefined,
                service_name: b.service_name,
                service_category: b.service_category,
                booking_date: b.booking_date,
                booking_time: b.booking_time,
                duration: b.duration,
                price: b.price,
                referral_code: profile.referral_code || undefined,
                booking_id: b.id,
              },
            });
            if (emailErr) throw emailErr;
            emailsSent++;
            didSomething = true;
          } catch (err) {
            console.error("Resend email failed for booking", b.id, err);
            failures++;
          }
        }

        if (sendSms && profile?.phone) {
          try {
            const { error: smsErr } = await supabase.functions.invoke("send-booking-sms", {
              body: {
                type,
                phone: profile.phone,
                service_name: b.service_name,
                booking_date: b.booking_date,
                booking_time: b.booking_time,
                duration: b.duration,
                price: b.price,
              },
            });
            if (smsErr) throw smsErr;
            smsSent++;
            didSomething = true;
          } catch (err) {
            console.error("Resend SMS failed for booking", b.id, err);
            failures++;
          }
        }

        if (!didSomething) skipped++;

        setProgress({ done: i + 1, total: filtered.length });
        // gentle pacing to avoid hammering the edge functions
        await new Promise((r) => setTimeout(r, 120));
      }

      setLastResult({ emailsSent, smsSent, failures, skipped });
      toast({
        title: "Resend complete",
        description: `Emails: ${emailsSent} • SMS: ${smsSent} • Skipped: ${skipped} • Failures: ${failures}`,
      });
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Resend failed",
        description: err?.message || "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-card border-border/50 max-w-xl">
      <CardHeader>
        <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
          <Bell size={16} />
          Resend Booking Notifications
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-muted-foreground text-sm font-light leading-relaxed">
          Re-send booking confirmation notifications for every booking in a date/time range.
          Use this if email or SMS notifications were missed due to an outage. Admin
          recipients are pulled automatically from the database.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Start date</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Start time</Label>
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">End date</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">End time</Label>
            <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </div>
        </div>

        <div className="space-y-3 pt-2 border-t border-border/40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <Mail size={14} className="text-muted-foreground" />
              <span>Send emails</span>
            </div>
            <Switch checked={sendEmail} onCheckedChange={setSendEmail} />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <MessageSquare size={14} className="text-muted-foreground" />
              <span>Send SMS</span>
            </div>
            <Switch checked={sendSms} onCheckedChange={setSendSms} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Include cancelled bookings</span>
            <Switch checked={includeCancelled} onCheckedChange={setIncludeCancelled} />
          </div>
        </div>

        <Button onClick={handleResend} disabled={busy} className="w-full">
          {busy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {progress ? `Resending ${progress.done}/${progress.total}…` : "Working…"}
            </>
          ) : (
            "Resend Notifications"
          )}
        </Button>

        {lastResult && !busy && (
          <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t border-border/40">
            <p>✉️ Emails sent: <span className="text-foreground">{lastResult.emailsSent}</span></p>
            <p>📱 SMS sent: <span className="text-foreground">{lastResult.smsSent}</span></p>
            <p>⏭️ Skipped (no contact info): <span className="text-foreground">{lastResult.skipped}</span></p>
            <p>⚠️ Failures: <span className="text-foreground">{lastResult.failures}</span></p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
